import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    IconButton,
    Popover,
    Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { intl, DmsPage, fetchData, HTTP_STATUS } from "dmsbasereact";
import { DmsAnalytics, useDmsAnalyticsContext, VIEW_MODE_OPTIONS, TreeExpandToggle } from "../../components/DmsAnalytics";

// View Mode Filter - Saldo/Venda/Sem Filtro
const ViewModeFilter = () => {
    const { viewMode, setViewMode, refresh } = useDmsAnalyticsContext();

    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
            // Trigger refresh to reload data with new filter
            setTimeout(() => refresh(), 0);
        }
    }, [setViewMode, refresh]);

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                {intl.get("Analytics.ViewMode.Label").d("Visualização")}
            </Typography>
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                fullWidth
                sx={{
                    "& .MuiToggleButton-root": {
                        py: 0.75,
                        fontSize: "0.75rem",
                        textTransform: "none",
                    },
                    "& .Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },
                    },
                }}
            >
                <ToggleButton value={VIEW_MODE_OPTIONS.ALL}>
                    {intl.get("Analytics.ViewMode.All").d("Sem Filtro")}
                </ToggleButton>
                <ToggleButton value={VIEW_MODE_OPTIONS.SALES}>
                    {intl.get("Analytics.ViewMode.Sales").d("Venda")}
                </ToggleButton>
                <ToggleButton value={VIEW_MODE_OPTIONS.STOCK}>
                    {intl.get("Analytics.ViewMode.Stock").d("Saldo")}
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
};

// Product and Batch Filters - to be rendered inside the Filters popover
const ProductBatchFilters = () => {
    const {
        crossFilters,
        toggleCrossFilter,
        clearCrossFilterType,
    } = useDmsAnalyticsContext();

    const [products, setProducts] = useState([]);
    const [batches, setBatches] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingBatches, setLoadingBatches] = useState(false);

    // Get currently selected product and batch from crossFilters
    const selectedProduct = crossFilters.product?.[0]?.value || "";
    const selectedBatch = crossFilters.batch?.[0]?.value || "";

    // Fetch products on mount
    useEffect(() => {
        const loadProducts = async () => {
            setLoadingProducts(true);
            try {
                const response = await fetchData("/analytics/filter-options/products", "GET", true);
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data?.products) {
                    setProducts(response.data.data.products);
                }
            } catch (error) {
                console.error("Error loading products:", error);
            } finally {
                setLoadingProducts(false);
            }
        };
        loadProducts();
    }, []);

    // Fetch batches when product changes
    useEffect(() => {
        const loadBatches = async () => {
            if (!selectedProduct) {
                setBatches([]);
                return;
            }
            setLoadingBatches(true);
            try {
                const response = await fetchData(
                    `/analytics/filter-options/batches?product_pk=${selectedProduct}`,
                    "GET",
                    true
                );
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data?.batches) {
                    setBatches(response.data.data.batches);
                }
            } catch (error) {
                console.error("Error loading batches:", error);
            } finally {
                setLoadingBatches(false);
            }
        };
        loadBatches();
    }, [selectedProduct]);

    // Handle product change
    const handleProductChange = useCallback(
        (event) => {
            const value = event.target.value;
            clearCrossFilterType("product");
            clearCrossFilterType("batch");
            setBatches([]);

            if (value) {
                const product = products.find((p) => p.value === value);
                if (product) {
                    toggleCrossFilter("product", product.value, product.label);
                }
            }
        },
        [products, toggleCrossFilter, clearCrossFilterType]
    );

    // Handle batch change
    const handleBatchChange = useCallback(
        (event) => {
            const value = event.target.value;
            clearCrossFilterType("batch");

            if (value) {
                const batch = batches.find((b) => b.value === value);
                if (batch) {
                    toggleCrossFilter("batch", batch.value, batch.label);
                }
            }
        },
        [batches, toggleCrossFilter, clearCrossFilterType]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* View Mode Filter */}
            <ViewModeFilter />

            <Divider />

            {/* Product Filter */}
            <FormControl fullWidth size="small">
                <InputLabel>{intl.get("Analytics.Filter.Product").d("Produto")}</InputLabel>
                <Select
                    value={selectedProduct}
                    onChange={handleProductChange}
                    label={intl.get("Analytics.Filter.Product").d("Produto")}
                    disabled={loadingProducts}
                    endAdornment={loadingProducts ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
                    MenuProps={{
                        disablePortal: false,
                        PaperProps: { sx: { maxHeight: 200 } },
                    }}
                >
                    <MenuItem value="">
                        <em>{intl.get("Analytics.Filter.All").d("Todos")}</em>
                    </MenuItem>
                    {products.map((product) => (
                        <MenuItem key={product.value} value={product.value}>
                            {product.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Batch Filter */}
            <FormControl fullWidth size="small">
                <InputLabel>{intl.get("Analytics.Filter.Batch").d("Lote")}</InputLabel>
                <Select
                    value={selectedBatch}
                    onChange={handleBatchChange}
                    label={intl.get("Analytics.Filter.Batch").d("Lote")}
                    disabled={!selectedProduct || loadingBatches}
                    endAdornment={loadingBatches ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
                    MenuProps={{
                        disablePortal: false,
                        PaperProps: { sx: { maxHeight: 200 } },
                    }}
                >
                    <MenuItem value="">
                        <em>{intl.get("Analytics.Filter.All").d("Todos")}</em>
                    </MenuItem>
                    {batches.map((batch) => (
                        <MenuItem key={batch.value} value={batch.value}>
                            {batch.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export const TraceabilityAnalyticsPage = () => {
    const traceabilityRef = useRef(null);
    const [expandState, setExpandState] = useState("collapsed");
    const [legendAnchor, setLegendAnchor] = useState(null);

    const handleExpandAll = useCallback(() => {
        traceabilityRef.current?.expandAll();
    }, []);

    const handleCollapseAll = useCallback(() => {
        traceabilityRef.current?.collapseAll();
    }, []);

    return (
        <DmsPage
            name={intl.get("Analytics.Traceability.Title").d("Rastreabilidade")}
            manual_slug="analytics-traceability"
        >
            <DmsAnalytics.Card>
                <DmsAnalytics.Header
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {intl.get("Analytics.Traceability.Subtitle").d("From Sale to Patient")}
                            <Tooltip title={intl.get("Analytics.Traceability.Legend").d("Legend")}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setLegendAnchor(e.currentTarget)}
                                    sx={{ color: "white", opacity: 0.8, "&:hover": { opacity: 1 } }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                            <Popover
                                open={Boolean(legendAnchor)}
                                anchorEl={legendAnchor}
                                onClose={() => setLegendAnchor(null)}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                PaperProps={{
                                    sx: {
                                        p: 3,
                                        maxWidth: 440,
                                        borderRadius: 2,
                                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                                    },
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "#1a365d", borderBottom: "2px solid #1a365d", pb: 1 }}>
                                    {intl.get("Analytics.Traceability.Legend").d("Legend")}
                                </Typography>

                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#1a365d", fontSize: "0.85rem" }}>
                                    {intl.get("Analytics.Traceability.LegendLevels").d("Distribution Chain Levels")}
                                </Typography>
                                <Box sx={{ pl: 1, mb: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem", display: "flex", gap: 0.5 }}>
                                        <Box component="span" sx={{ fontWeight: 700, color: "#1a365d", minWidth: 28 }}>N1:</Box>
                                        {intl.get("Analytics.Traceability.LegendN1Desc").d("First level - Distributors and Intermediaries that receive from Eurofarma")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem", display: "flex", gap: 0.5 }}>
                                        <Box component="span" sx={{ fontWeight: 700, color: "#1a365d", minWidth: 28 }}>N2:</Box>
                                        {intl.get("Analytics.Traceability.LegendN2Desc").d("Second level - Clients of N1 distributors")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem", display: "flex", gap: 0.5 }}>
                                        <Box component="span" sx={{ fontWeight: 700, color: "#1a365d", minWidth: 28 }}>Nn:</Box>
                                        {intl.get("Analytics.Traceability.LegendNnDesc").d("Nth level - Continues based on the checkout chain depth")}
                                    </Typography>
                                </Box>

                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#1a365d", fontSize: "0.85rem" }}>
                                    {intl.get("Analytics.Traceability.LegendColumns").d("Columns")}
                                </Typography>
                                <Box sx={{ pl: 1, mb: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{intl.get("Analytics.Traceability.EurofarmaSales").d("Eurofarma Sales")}:</Box>{" "}
                                        {intl.get("Analytics.Traceability.LegendVendaEurofarmaDesc").d("Quantity sold/shipped by Eurofarma")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{intl.get("Analytics.Traceability.LegendDispensacaoNx").d("Dispensation Nx")}:</Box>{" "}
                                        {intl.get("Analytics.Traceability.LegendDispensacaoNxDesc").d("Quantity dispensed by institutions at level N")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{intl.get("Analytics.Traceability.LegendVendaRepasseNx").d("Sale or Transfer Nx")}:</Box>{" "}
                                        {intl.get("Analytics.Traceability.LegendVendaRepasseNxDesc").d("Quantity sold/transferred by institutions at level N")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{intl.get("Analytics.Traceability.Saldo").d("Balance")}:</Box>{" "}
                                        {intl.get("Analytics.Traceability.LegendSaldoDesc").d("Eurofarma Sales - Dispensations - Returns")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{intl.get("Analytics.Traceability.Devolucao").d("Returns")}:</Box>{" "}
                                        {intl.get("Analytics.Traceability.LegendDevolucaoDesc").d("Quantity returned to Eurofarma")}
                                    </Typography>
                                </Box>

                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#1a365d", fontSize: "0.85rem" }}>
                                    {intl.get("Analytics.Traceability.LegendInteraction").d("Interaction")}
                                </Typography>
                                <Box sx={{ pl: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                                        {intl.get("Analytics.Traceability.LegendInteractionDesc").d("Click on a row in the tree to filter the product summary and patient dispensations.")}
                                    </Typography>
                                </Box>
                            </Popover>
                        </Box>
                    }
                    actions={
                        <TreeExpandToggle
                            expandState={expandState}
                            onExpandAll={handleExpandAll}
                            onCollapseAll={handleCollapseAll}
                        />
                    }
                />
                <DmsAnalytics.Chips showApplyButton={false} />
                <DmsAnalytics.Body>
                    <DmsAnalytics.Traceability
                        ref={traceabilityRef}
                        url="/analytics/traceability/v2"
                        onExpandStateChange={setExpandState}
                    />
                </DmsAnalytics.Body>
                <DmsAnalytics.Filters showApplyButton>
                    <ProductBatchFilters />
                </DmsAnalytics.Filters>
            </DmsAnalytics.Card>
        </DmsPage>
    );
};
